import { useCallback, useEffect, useState } from 'react';
import { Calendar, MapPin, Clock, LayoutGrid, List, Plus, Pencil, Trash2, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { fmtDate } from '@/lib/upload';
import { usePageContent, useSiteData } from '@/context/SiteDataContext';
import { useEditMode } from '@/context/EditModeContext';
import PageHero from '@/components/PageHero';
import EventDialog from '@/components/editable/EventDialog';

const LONG = { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' };
const sortEvents = (list) => [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || String(a.date).localeCompare(String(b.date)));

export default function Events() {
  const c = usePageContent('events');
  const { loadPageContent } = useSiteData();
  const { editMode } = useEditMode();
  const [events, setEvents] = useState([]);
  const [view, setView] = useState(null);
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [dragId, setDragId] = useState(null);

  const load = useCallback(() => api.get('/events').then(r => setEvents(sortEvents(r.data || []))).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const layout = view || c.layout || 'list';
  const filtered = events.filter(e => filter === 'all' || e.department === filter);
  const departments = ['all', ...new Set(events.map(e => e.department).filter(Boolean))];

  const saveSetting = async (patch) => { await api.patch('/site/page-content/events', patch); loadPageContent('events'); };
  const remove = async (e) => {
    if (!window.confirm(`Delete "${e.title}"?`)) return;
    await api.delete(`/events/${e.id}`); toast.success('Event deleted'); load();
  };
  const dropOn = async (target) => {
    if (!dragId || dragId === target.id) return;
    const ids = events.map(e => e.id).filter(id => id !== dragId);
    ids.splice(ids.indexOf(target.id), 0, dragId);
    setDragId(null);
    setEvents(ids.map(id => events.find(e => e.id === id)));
    await Promise.all(ids.map((id, i) => api.patch(`/events/${id}`, { order: i })));
    load();
  };
  const newEvent = () => setEditing({ title: '', date: '', time: '', location: '', description: '', image: '', department: '', registration_url: '', order: events.length });

  const card = (e) => (
    <EventCard key={e.id} e={e} layout={layout} editMode={editMode}
      onEdit={() => setEditing({ ...e })} onDelete={() => remove(e)}
      onDragStart={() => setDragId(e.id)} onDrop={() => dropOn(e)} dragging={dragId === e.id} />
  );

  return (
    <div>
      <PageHero slug="events" overline="Gather With Us" heading="Events" text="Convocations, conferences, and gatherings across the jurisdiction." />

      <section className="py-16" style={{ backgroundColor: c.section_bg_color || '#ffffff' }} data-testid="events-section">
        <div className="max-w-7xl mx-auto px-6">
          {editMode && (
            <div className="mb-8 rounded-lg border-2 border-dashed border-gold/60 bg-gold/5 p-4 flex flex-wrap items-center gap-3 text-sm" data-testid="events-edit-toolbar">
              <button onClick={newEvent} data-testid="events-add-btn" className="btn-royal text-sm py-2 px-4 inline-flex items-center gap-1"><Plus className="w-4 h-4" /> Add Event</button>
              <label className="flex items-center gap-2">Default layout
                <select value={c.layout || 'list'} onChange={(e) => saveSetting({ layout: e.target.value })} data-testid="events-layout-select" className="border rounded px-2 py-1">
                  <option value="list">List</option><option value="grid">Grid</option><option value="compact">Compact</option>
                </select></label>
              <label className="flex items-center gap-2">Section background
                <input type="color" value={c.section_bg_color || '#ffffff'} onChange={(e) => saveSetting({ section_bg_color: e.target.value })} data-testid="events-bg-color" /></label>
              <label className="flex items-center gap-1"><input type="checkbox" checked={c.show_filters !== false} onChange={(e) => saveSetting({ show_filters: e.target.checked })} data-testid="events-show-filters" /> Show filters & view toggle</label>
              <span className="text-xs text-charcoal-muted">Drag cards to reorder.</span>
            </div>
          )}

          {c.show_filters !== false && events.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
              <div className="flex gap-2 flex-wrap">
                {departments.map(d => (
                  <button key={d} data-testid={`filter-${d}`} onClick={() => setFilter(d)} className={`px-4 py-2 rounded-full text-sm font-medium transition ${filter === d ? 'bg-royal text-white' : 'bg-gray-100 text-charcoal hover:bg-gray-200'}`}>
                    {d === 'all' ? 'All' : d}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 bg-gray-100 rounded-full p-1">
                <button data-testid="events-view-list" onClick={() => setView('list')} className={`px-4 py-2 rounded-full text-sm flex items-center gap-1 ${layout === 'list' ? 'bg-white shadow' : ''}`}><List className="w-4 h-4" /> List</button>
                <button data-testid="events-view-grid" onClick={() => setView('grid')} className={`px-4 py-2 rounded-full text-sm flex items-center gap-1 ${layout === 'grid' ? 'bg-white shadow' : ''}`}><LayoutGrid className="w-4 h-4" /> Grid</button>
              </div>
            </div>
          )}

          {filtered.length === 0 && <div className="text-center py-16 text-charcoal-muted" data-testid="events-empty">There are no upcoming events at this time. Please check back soon.</div>}
          <div className={layout === 'list' ? 'space-y-6' : layout === 'grid' ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-8' : 'grid md:grid-cols-2 gap-4'}>
            {filtered.map(card)}
          </div>
        </div>
      </section>
      {editing && <EventDialog event={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function EventCard({ e, layout, editMode, onEdit, onDelete, onDragStart, onDrop, dragging }) {
  const style = e.bg_color ? { backgroundColor: e.bg_color } : undefined;
  const dragProps = editMode ? { draggable: true, onDragStart, onDragOver: (ev) => ev.preventDefault(), onDrop } : {};
  const tools = editMode && (
    <div className="absolute top-2 right-2 z-10 flex gap-1">
      <span className="p-1.5 bg-white/90 rounded cursor-grab"><GripVertical className="w-4 h-4 text-charcoal-muted" /></span>
      <button onClick={onEdit} data-testid={`event-edit-${e.id}`} className="p-1.5 bg-royal text-white rounded"><Pencil className="w-4 h-4" /></button>
      <button onClick={onDelete} data-testid={`event-delete-${e.id}`} className="p-1.5 bg-red-600 text-white rounded"><Trash2 className="w-4 h-4" /></button>
    </div>
  );
  const register = e.registration_url && (
    <a href={e.registration_url} target="_blank" rel="noopener noreferrer" data-testid={`event-register-${e.id}`} className="btn-royal text-sm">{e.registration_label || 'Register'}</a>
  );
  const base = `relative card-tactile overflow-hidden ${dragging ? 'opacity-40' : ''}`;

  if (layout === 'compact') {
    return (
      <div {...dragProps} style={style} data-testid={`event-card-${e.id}`} className={`${base} p-5 flex gap-4 items-center`}>
        {tools}
        <div className="w-16 text-center shrink-0">
          <div className="text-xs uppercase text-gold font-bold">{fmtDate(e.date, { month: 'short' })}</div>
          <div className="font-serif text-3xl text-royal font-bold">{fmtDate(e.date, { day: 'numeric' })}</div>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-serif text-lg text-royal font-bold">{e.title}</h3>
          <div className="text-sm text-charcoal-muted">{[e.time, e.location].filter(Boolean).join(' · ')}</div>
        </div>
        {register}
      </div>
    );
  }
  if (layout === 'grid') {
    return (
      <div {...dragProps} style={style} data-testid={`event-card-${e.id}`} className={base}>
        {tools}
        {e.image && <div className="aspect-[4/3] overflow-hidden"><img src={e.image} alt={e.title} className="w-full h-full object-cover" /></div>}
        <div className="p-6">
          <div className="text-xs text-gold uppercase tracking-widest font-semibold">{fmtDate(e.date, { month: 'long', day: 'numeric' })}{e.time ? ` · ${e.time}` : ''}</div>
          <h3 className="font-serif text-xl text-royal font-bold mt-2">{e.title}</h3>
          {e.location && <p className="text-charcoal-muted text-sm mt-2">{e.location}</p>}
          {register && <div className="mt-4">{register}</div>}
        </div>
      </div>
    );
  }
  return (
    <div {...dragProps} style={style} data-testid={`event-card-${e.id}`} className={`${base} grid md:grid-cols-4`}>
      {tools}
      {e.image && <div className="md:col-span-1 aspect-[4/3] md:aspect-auto overflow-hidden"><img src={e.image} alt={e.title} className="w-full h-full object-cover" /></div>}
      <div className={`${e.image ? 'md:col-span-3' : 'md:col-span-4'} p-8 flex flex-col justify-center`}>
        {e.department && <div className="text-xs text-gold tracking-widest uppercase font-semibold">{e.department}</div>}
        <h3 className="font-serif text-2xl text-royal font-bold mt-2">{e.title}</h3>
        {e.description && <p className="text-charcoal-muted mt-2 whitespace-pre-line">{e.description}</p>}
        <div className="mt-4 flex flex-wrap gap-6 text-sm text-charcoal-muted">
          {e.date && <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-royal" /> {fmtDate(e.date, LONG)}</div>}
          {e.time && <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-royal" /> {e.time}</div>}
          {e.location && <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-royal" /> {e.location}</div>}
        </div>
        {register && <div className="mt-6">{register}</div>}
      </div>
    </div>
  );
}
